import { Button, Card, CardContent, CardHeader, CardTitle, Separator } from '@repo/ui'
import { Building2, Database, Settings2, TriangleAlert, Users } from 'lucide-react'
import { useState } from 'react'
import type { OrgUser } from '@/api/org/users'
import type { ProjectApiResponse } from '@/api/project/list'
import type { UserOrgProfile } from '@/api/user/profile'
import { DeleteProjectDialog } from '@/components/DeleteProjectDialog'
import { ProjectForm } from '@/components/ProjectDialog'
import { OrganisationAccountSettings } from './OrganisationAccountSettings'
import { OrganisationUsersSettings } from './OrganisationUsersSettings'
import { ScopedContentSettings } from './ScopedContentSettings'
import { SettingsDialogFrame, SettingsSection, useScopedSettings } from './ScopedSettings'
import type { OrganisationSettingsSection, WorkspaceSettingsSection } from './ScopedSettings'

function monogramOf(name: string): string {
  return name
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
}

export function ScopedSettingsDialogHost({
  userOrgProfile,
  projects,
  orgUsers,
  onAuthorityChanged
}: {
  userOrgProfile: UserOrgProfile
  projects: Array<ProjectApiResponse>
  orgUsers: Array<OrgUser>
  onAuthorityChanged: () => void | Promise<void>
}) {
  const { target, openSettings, closeSettings } = useScopedSettings()
  const [deleteProjectId, setDeleteProjectId] = useState<number>()

  if (target === undefined) return null
  if (target.scope === 'profile') return null

  if (target.scope === 'organisation') {
    const sections = [
      { value: 'account' as const, label: 'Account', icon: <Building2 aria-hidden="true" /> },
      { value: 'users' as const, label: 'Users', icon: <Users aria-hidden="true" /> },
      ...(userOrgProfile.enabled_features.global_content
        ? [{ value: 'content' as const, label: 'Global content', icon: <Database aria-hidden="true" /> }]
        : [])
    ]
    const activeSection: OrganisationSettingsSection =
      target.section === 'content' && !userOrgProfile.enabled_features.global_content ? 'account' : target.section

    return (
      <SettingsDialogFrame
        open
        title={userOrgProfile.org.name}
        description="Organisation settings"
        monogram={monogramOf(userOrgProfile.org.name)}
        sections={sections}
        activeSection={activeSection}
        onOpenChange={(open) => !open && closeSettings()}
        onSectionChange={(section) =>
          openSettings({
            scope: 'organisation',
            section,
            ...(section === 'content' ? { contentKind: target.contentKind ?? 'files' } : {})
          })
        }
      >
        {activeSection === 'account' && <OrganisationAccountSettings userOrgProfile={userOrgProfile} />}
        {activeSection === 'users' && <OrganisationUsersSettings users={orgUsers} userOrgProfile={userOrgProfile} />}
        {activeSection === 'content' && (
          <ScopedContentSettings
            userOrgProfile={userOrgProfile}
            kind={target.contentKind ?? 'files'}
            {...(target.contentItemId !== undefined ? { initialContentId: target.contentItemId } : {})}
            onKindChange={(contentKind) => openSettings({ scope: 'organisation', section: 'content', contentKind })}
            onAuthorityChanged={onAuthorityChanged}
          />
        )}
      </SettingsDialogFrame>
    )
  }

  const project = projects.find((candidate) => candidate.projectId === target.projectId)
  const sections = [
    { value: 'general' as const, label: 'General', icon: <Settings2 aria-hidden="true" /> },
    { value: 'content' as const, label: 'Content', icon: <Database aria-hidden="true" /> },
    { value: 'danger' as const, label: 'Danger zone', icon: <TriangleAlert aria-hidden="true" /> }
  ]

  if (project === undefined) {
    return (
      <SettingsDialogFrame
        open
        title="Workspace settings"
        description="The selected workspace is no longer available."
        sections={sections}
        activeSection="general"
        onOpenChange={(open) => !open && closeSettings()}
        onSectionChange={() => undefined}
      >
        <p role="alert" className="text-sm text-muted-foreground">
          This workspace may have been removed or your access may have changed.
        </p>
      </SettingsDialogFrame>
    )
  }

  const setWorkspaceSection = (section: WorkspaceSettingsSection) =>
    openSettings({
      scope: 'workspace',
      projectId: project.projectId,
      section,
      ...(section === 'content' ? { contentKind: target.contentKind ?? 'files' } : {})
    })

  return (
    <>
      <SettingsDialogFrame
        open
        title={project.title}
        description="Workspace settings"
        monogram={monogramOf(project.title)}
        sections={sections}
        activeSection={target.section}
        onOpenChange={(open) => !open && closeSettings()}
        onSectionChange={setWorkspaceSection}
      >
        {target.section === 'general' && (
          <SettingsSection id="workspace-general-heading" title="General" summary="Workspace details and membership.">
            <ProjectForm
              key={`${project.projectId}:${project.updatedOnTsMs}`}
              mode="edit"
              project={project}
              orgUsers={orgUsers}
              onComplete={() => void onAuthorityChanged()}
            />
          </SettingsSection>
        )}
        {target.section === 'content' && (
          <ScopedContentSettings
            projectId={project.projectId}
            userOrgProfile={userOrgProfile}
            kind={target.contentKind ?? 'files'}
            {...(target.contentItemId !== undefined ? { initialContentId: target.contentItemId } : {})}
            onKindChange={(contentKind) =>
              openSettings({
                scope: 'workspace',
                projectId: project.projectId,
                section: 'content',
                contentKind
              })
            }
            onAuthorityChanged={onAuthorityChanged}
          />
        )}
        {target.section === 'danger' && (
          <SettingsSection
            id="workspace-danger-heading"
            title="Danger zone"
            summary="Actions here permanently affect this workspace."
          >
            <Card className="border-destructive/35">
              <CardHeader>
                <CardTitle className="text-base">Delete workspace</CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="flex flex-col gap-4 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="max-w-xl text-sm text-muted-foreground">
                  Permanently delete this workspace, including its content, threads, and data.
                </p>
                <Button type="button" variant="destructive" onClick={() => setDeleteProjectId(project.projectId)}>
                  Delete workspace
                </Button>
              </CardContent>
            </Card>
          </SettingsSection>
        )}
      </SettingsDialogFrame>
      <DeleteProjectDialog
        open={deleteProjectId === project.projectId}
        projectId={project.projectId}
        projectTitle={project.title}
        orgSlug={userOrgProfile.org.slug}
        onClose={() => setDeleteProjectId(undefined)}
        onDeleted={async () => {
          await onAuthorityChanged()
          closeSettings()
        }}
      />
    </>
  )
}
